import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Check, ChevronDown } from "lucide-react";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { MultiSelect } from "../multiselect/multiselectCheckbox";
import { DatePickerWithRange } from "../date-range/date-rangePicker";
import { toast } from "../use-toast";
import { SearchComboBox } from "@/components/ui/globalsearch/search-combo-box";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetStaffsQuery } from "@/services/staffsApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface FilterTypes {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue?: any;
  setFilter?: any;
  setSearchCriteria?: any;
  filterData?: any;
  filterDisplay?: any;
  setInputValue?: React.Dispatch<React.SetStateAction<string>>; //some table dont have search
  onReset?: () => void;
}

interface UserType {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  own_member_id?: string;
  own_coach_id?: string;
  own_staff_id?: string;
}

const TableFilters = ({
  isOpen,
  setOpen,
  initialValue,
  filterData,
  setFilter,
  setSearchCriteria,
  setInputValue,
  filterDisplay,
  onReset,
}: FilterTypes) => {

  return (
    <div>
      <Sheet open={isOpen} onOpenChange={() => setOpen(false)}>
        <SheetContent className="!h-screen">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <Separator className=" h-[1px] rounded-full my-2" />
          <div className="py-2 space-y-4 ">
            {filterDisplay &&
              filterDisplay?.map((element: any) => {
                if (element.type == "select") {
                  return (
                    <Select
                      key={element.name}
                      disabled={element.disabled}
                      name={element.name}
                      value={filterData[element.name]}
                      onValueChange={(value) => {
                        const selectedOption = element.options?.find(
                          (st: any) => String(st.id) === String(value)
                        );
                        // Pass both value and the full option for handlers that need metadata
                        element.function(value, selectedOption);
                      }}
                    >
                      <SelectTrigger
                        className="capitalize"
                        floatingLabel={element.label}
                      >
                        <SelectValue placeholder={"Select " + element.label} />
                      </SelectTrigger>
                      <SelectContent className="capitalize">
                        {element.options?.map((st: any, index: number) => (
                          <SelectItem key={index} value={String(st.id)}>
                            {st.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }
                if (element.type == "combobox") {
                  return (
                    <Combobox
                      key={element.name}
                      disabled={element.disabled}
                      defaultValue={filterData[element.name]}
                      setFilter={element.function}
                      list={element.options}
                      name={element.name}
                      label={element.label}
                    />
                  );
                }
                if (element.type == "date-range") {
                  return (
                    <div className="w-full " key={element.name}>
                      <DatePickerWithRange
                        disabled={element.disabled}
                        name={element.name}
                        value={{
                          start_date: filterData.start_date, // Access start_date directly
                          end_date: filterData.end_date, // Access end_date directly
                        }}
                        onValueChange={(dates) => {
                          // Call your updated function
                          element.function(dates);
                        }}
                        label={"Select " + element.label}
                        className="w-full" // Ensure full width
                      />
                    </div>
                  );
                }

                if (element.type == "percentage") {
                  return (
                    <FloatingLabelInput
                      key={element.name}
                      disabled={element.disabled}
                      type={"number"}
                      min={0}
                      defaultValue={filterData[element.name]}
                      max={100}
                      name={element.name}
                      label={element.label}
                      onChange={element.function}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        if (Number(target.value) > 100) {
                          target.value = "100";
                        }
                      }}
                    />
                  );
                }
                if (element.type == "number") {
                  return (
                    <FloatingLabelInput
                      key={element.name}
                      disabled={element.disabled}
                      defaultValue={filterData[element.name]}
                      type={"number"}
                      min={0}
                      id={element.name}
                      name={element.name}
                      label={element.label}
                      onChange={element.function}
                    />
                  );
                }

                if (element.type === "multiselect") {
                  return (
                    <MultiSelect
                      key={element.name}
                      disabled={element.disabled}
                      floatingLabel={element.label.replace(/_/g, " ")}
                      options={element.options}
                      defaultValue={filterData[element.name] || []} // Ensure defaultValue is always an array
                      onValueChange={(selectedValues) => {
                        element.function(selectedValues); // Pass selected values to state handler
                      }}
                      placeholder={"Select " + element.label.replace(/_/g, " ")}
                      variant="inverted"
                      maxCount={1}
                      className="capitalize focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    />
                  );
                }
              })}
            <div className="gap-3 flex ">
              <Button
                type="submit"
                variant={"outline"}
                onClick={() => {
                  setFilter({});
                  setSearchCriteria(initialValue);
                  setInputValue && setInputValue("");
                  onReset && onReset(); // Add this line
                  setOpen(false);
                }}
                className="border-primary text-black gap-1 font-semibold w-full"
              >
                Reset
              </Button>
              <Button
                type="submit"
                onClick={() => {
                  const dateRangeFilter = filterDisplay?.find(
                    (element: any) => element.type == "date-range"
                  );

                  let isValidDateRange = true; // To track the validity of the date range

                  if (dateRangeFilter) {
                    const { start_date, end_date } = filterData;

                    // If one date is present and the other is missing, show the error toast
                    if (
                      (start_date && !end_date) ||
                      (!start_date && end_date)
                    ) {
                      toast({
                        variant: "destructive",
                        description: "Must be a valid date range.",
                      });
                      isValidDateRange = false; // Set to false if invalid
                      return;
                    }

                    // If both dates are missing, log the message and skip applying the date range
                    if (!start_date && !end_date) {
                      isValidDateRange = false; // Set to false if no date range is provided
                    }
                  }

 
                  // Proceed with applying other filters regardless of the date range validity
                  setSearchCriteria((prev: any) => ({
                    ...prev,
                    ...filterData, // Apply all filter data
                    ...(isValidDateRange
                      ? {}
                      : { start_date: undefined, end_date: undefined }), // Only apply date range if valid
                    offset: 0,
                    // search_key: prev.search_key,
                    sort_key: initialValue.sort_key,
                    sort_order: initialValue.sort_order,
                  }));

                  // setInputValue && setInputValue("")

                  // Close the side panel after applying filters
                  setOpen(false);
                }}
                className="bg-primary text-black space-x-2 font-semibold w-full"
              >
                <i className="fa fa-filter "></i>
                Apply filters
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

interface ComboboxType {
  list?: {
    label: string;
    value: string;
  }[];
  defaultValue?: string;
  setFilter?: any;
  name?: string;
  label?: string;
  disabled?: boolean;
}

function Combobox({
  list,
  setFilter,
  name,
  defaultValue,
  label,
  disabled,
}: ComboboxType) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    list?.find((list) => list.value == defaultValue)?.label ?? ""
  );
  return (
    <div >
      <Popover  open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <div className="relative">
            <span className="absolute p-0 text-[11px] left-2 -top-1.5 px-1 bg-white capitalize">
              {label}
            </span>
            <Button
              disabled={disabled}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="border-[1px] hover:bg-transparent w-full justify-between font-normal capitalize"
            >
              {value
                ? list && list?.find((list) => list.label == value)?.label
                : "Select " + label?.toLowerCase()}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className="h-fit w-[330px] max-h-28 p-0" side="bottom">
          <Command>
            <CommandInput placeholder={`Search ${label}`} />
            <CommandEmpty>No list found.</CommandEmpty>
            <CommandList className="shadow-sm  custom-scrollbar">
              <CommandGroup>
                {list &&
                  list?.map((item) => (
                    <CommandItem
                      className="capitalize"
                      key={item.value}
                      value={item.label}
                      onSelect={(currentValue) => {
                        const selectedItem = list?.find((list) => list.label === currentValue);
                        setValue(currentValue == value ? "" : currentValue);
                        setFilter(selectedItem?.value);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === item.label ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {item.label}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>

  );
}



export default TableFilters;
